/**
 * Сессия партнёра.
 *
 * Бренд приходит из токена, а не из запроса, — кабинет не может назвать чужой
 * бренд. Здесь мы только храним то, что сервер уже решил.
 */
import type { ConsoleMe } from '@allswipes/client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api } from './api';

interface State {
  me: ConsoleMe | null;
  loading: boolean;
  signedIn: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<State | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<ConsoleMe | null>(null);
  const [loading, setLoading] = useState(Boolean(api.session));

  const refresh = useCallback(async () => {
    if (!api.session) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMe(await api.consoleMe());
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    return api.onSessionChange((token) => {
      if (!token) setMe(null);
      else void refresh();
    });
  }, [refresh]);

  const value = useMemo<State>(
    () => ({
      me,
      loading,
      signedIn: Boolean(api.session && me),
      refresh,
      signOut: () => api.setToken(null),
    }),
    [me, loading, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useConsole(): State {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useConsole вызван вне SessionProvider');
  return ctx;
}

/** Может ли роль запускать акции и проводить розыгрыш. */
export function canPublish(role: string | undefined): boolean {
  return role === 'owner' || role === 'manager';
}
